import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import without from "lodash/without";
import { CurrentUser } from "sdk/currentUser";
import { DiagnosticUser } from "sdk/diagnosticUser";
import { routes } from "~/constants";

interface State {
  csrfToken: string | null;
  currentUser: CurrentUser | null;
  currentDiagnosticUser: DiagnosticUser | null;
  hasLoadedInitial: boolean;
  hasLoadedDiagnosticInitial: boolean;
  isPotentiallyAuthenticated: boolean;
  isPotentiallyDiagnosticAuthenticated: boolean;
}

const AUTHENTICATED_KEY = "authenticated";
const DIAGNOSTIC_AUTHENTICATED_KEY = "diagnostic_authenticated";

const initialState: State = {
  csrfToken: null,
  currentUser: null,
  currentDiagnosticUser: null,
  hasLoadedInitial: false,
  hasLoadedDiagnosticInitial: false,
  isPotentiallyAuthenticated: !!localStorage.getItem(AUTHENTICATED_KEY),
  isPotentiallyDiagnosticAuthenticated: !!localStorage.getItem(
    DIAGNOSTIC_AUTHENTICATED_KEY,
  ),
};

const authenticationSlice = createSlice({
  name: "authentication",
  initialState,
  reducers: {
    setCsrfToken: (state, action: PayloadAction<string>) => {
      state.csrfToken = action.payload;
    },
    setCurrentUser: (state, action: PayloadAction<CurrentUser>) => {
      const currentUser = action.payload;

      state.currentUser = currentUser;
      state.hasLoadedInitial = true;
      state.isPotentiallyAuthenticated = true;

      localStorage.setItem(AUTHENTICATED_KEY, JSON.stringify(true));
    },
    setHasLoadedInitial: (state, action: PayloadAction<boolean>) => {
      state.hasLoadedInitial = action.payload;
    },
    setCurrentDiagnosticUser: (
      state,
      action: PayloadAction<DiagnosticUser>,
    ) => {
      const currentDiagnosticUser = action.payload;

      state.currentDiagnosticUser = currentDiagnosticUser;
      state.hasLoadedDiagnosticInitial = true;
      state.isPotentiallyDiagnosticAuthenticated = true;

      localStorage.setItem(DIAGNOSTIC_AUTHENTICATED_KEY, JSON.stringify(true));
    },
    setHasLoadedDiagnosticInitial: (state, action: PayloadAction<boolean>) => {
      state.hasLoadedDiagnosticInitial = action.payload;
    },
    setPotentiallyDiagnosticAuthenticated: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isPotentiallyDiagnosticAuthenticated = action.payload;
      if (action.payload) {
        localStorage.setItem(
          DIAGNOSTIC_AUTHENTICATED_KEY,
          JSON.stringify(true),
        );
      } else {
        localStorage.removeItem(DIAGNOSTIC_AUTHENTICATED_KEY);
      }
    },
    signOutDiagnosticUser: (state) => {
      state.currentDiagnosticUser = null;
      state.isPotentiallyDiagnosticAuthenticated = false;
      localStorage.removeItem(DIAGNOSTIC_AUTHENTICATED_KEY);

      const isAlreadyOnDiagnosticSignInPage =
        window.location.pathname === routes.diagnosticsSignIn;

      if (isAlreadyOnDiagnosticSignInPage) return;

      // Forces a reload, resetting state.
      window.location.href = routes.diagnosticsSignIn;
    },
    signOut: (state) => {
      state.csrfToken = null;
      state.currentUser = null;
      state.isPotentiallyAuthenticated = false;
      localStorage.removeItem(AUTHENTICATED_KEY);

      const isAlreadyOnSignInPage = window.location.pathname === routes.signIn;

      if (isAlreadyOnSignInPage) return;

      // Forces a reload, resetting state.
      window.location.href = routes.signIn;
    },
    // * This is not the best place for this, but to avoid a hasty abstraction,
    // * we'll leave it here for now.
    updateUserUi: (
      state,
      action: PayloadAction<Partial<CurrentUser["ui"]>>,
    ) => {
      if (!state.currentUser) return;

      state.currentUser.ui = {
        ...state.currentUser.ui,
        ...action.payload,
      };

      if (action.payload.hasClosedOnboardingMessage) {
        state.currentUser.alerts = without(
          state.currentUser.alerts,
          "onboarding",
        );
      }

      if (action.payload.hasClosedReferralMessage) {
        state.currentUser.alerts = without(
          state.currentUser.alerts,
          "refer-a-friend",
        );
      }

      if (action.payload.hasClosedTrialOneWeekLeft) {
        state.currentUser.alerts = without(
          state.currentUser.alerts,
          "trial-one-week-left",
        );
      }

      if (action.payload.hasClosedTrialThreeWeeksLeft) {
        state.currentUser.alerts = without(
          state.currentUser.alerts,
          "trial-three-weeks-left",
        );
      }
    },
  },
});

export const {
  signOut,
  signOutDiagnosticUser,
  setCsrfToken,
  setCurrentUser,
  setCurrentDiagnosticUser,
  setHasLoadedInitial,
  setHasLoadedDiagnosticInitial,
  setPotentiallyDiagnosticAuthenticated,
  updateUserUi,
} = authenticationSlice.actions;
export const { reducer } = authenticationSlice;
